import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { IwiEvent, IwiItemRef, IwiKind, IWI_NOTE_MAX } from '../types/iwi.types';

/**
 * RECORDING an I Want It. Phase 0: this is the whole feature. Nothing is sent.
 *
 * Deliberately NOT modelled on logScanEvent. That one is fire-and-forget and
 * swallows every failure, which is correct for analytics nobody was promised.
 * This is different: the requester is TOLD their interest was recorded, so a
 * failed write must reach them. Swallowing it would be this week's bug again —
 * a check that reports clean while doing nothing.
 */

const COLLECTION = 'iwiEvents';

export interface RecordIwiInput {
  item: IwiItemRef;
  kind?: IwiKind;
  /** Dollars as typed by a human; converted to cents here. Blank/invalid → null. */
  amountDollars?: string | number | null;
  note?: string | null;
  requesterUid?: string | null;
  requesterEmail?: string | null;
  lastKnownSaleId?: string | null;
  lastKnownSaleTitle?: string | null;
  lastKnownSaleEndedAt?: Date | null;
  source: IwiEvent['source'];
}

/** The stable join key. TTR code when there is one — it outlives the lot. */
export const iwiItemKey = (item: IwiItemRef): string => {
  const ttr = (item.ttrCode || '').trim();
  if (ttr) return ttr.toUpperCase();
  return `${item.auctionId || 'unknown'}_${item.lotId || 'unknown'}`;
};

/** Dollars (string or number, possibly '$1,200') → integer cents, or null. */
export const parseAmountToCents = (v: string | number | null | undefined): number | null => {
  if (v === null || v === undefined) return null;
  const s = String(v).replace(/[$,\s]/g, '');
  if (s === '') return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
};

/**
 * PURE. Builds exactly what gets written, so the shape is testable without
 * Firestore. Kept separate from the write for that reason alone.
 *
 * Note what is absent and must stay absent: any owner identity. This document
 * records who WANTED something, never who HAS it.
 */
export const buildIwiEvent = (input: RecordIwiInput): IwiEvent => {
  const item: IwiItemRef = {
    ttrCode: input.item.ttrCode || null,
    auctionId: input.item.auctionId || null,
    lotId: input.item.lotId || null,
    lotNumber: input.item.lotNumber || null,
    sku: input.item.sku || null,
    itemTitle: input.item.itemTitle || null,
  };
  const note = (input.note || '').trim();
  return {
    itemKey: iwiItemKey(item),
    item,
    kind: input.kind || 'ping',
    amountCents: parseAmountToCents(input.amountDollars),
    note: note ? note.slice(0, IWI_NOTE_MAX) : null,
    requesterUid: input.requesterUid || null,
    requesterEmail: input.requesterUid ? null : (input.requesterEmail || '').trim().toLowerCase() || null,
    lastKnownSaleId: input.lastKnownSaleId || null,
    lastKnownSaleTitle: input.lastKnownSaleTitle || null,
    lastKnownSaleEndedAt: input.lastKnownSaleEndedAt || null,
    // Phase 0 writes this and only this. Nothing is delivered, and the record
    // does not pretend otherwise.
    deliveryState: 'not_attempted',
    source: input.source,
    createdAt: serverTimestamp(),
  };
};

export interface RecordIwiResult {
  ok: boolean;
  id?: string;
  /** Present when ok === false. Shown to the requester — never swallowed. */
  error?: string;
}

/**
 * Write the event. One job, no preconditions: there is nothing to check against
 * because nothing is being delivered. When Phase 1 adds owner state and rate
 * limiting, those decisions belong in the RELAY step downstream of this write,
 * never as a guard in front of it.
 */
export async function recordIwi(input: RecordIwiInput): Promise<RecordIwiResult> {
  try {
    const ref = await addDoc(collection(db, COLLECTION), buildIwiEvent(input) as any);
    return { ok: true, id: ref.id };
  } catch (err: any) {
    return { ok: false, error: err?.message || 'Could not record that. Please try again.' };
  }
}
